import type { EstimateAssumption, EstimateExclusion, EstimateWarning, EstimatorInput } from "../../types/index.js";
import {
  applyItem,
  findItem,
  scaleAppliedItem,
  type AppliedLineItem,
  type ProjectCalculationOutput,
  type ProjectCalculatorFn
} from "./shared.js";

/**
 * The Attic Estimator (Phase 13B). `attic` had no calculation
 * methodology at all before this phase.
 *
 * Deliberately a bespoke calculator (not built on
 * `createInteriorRoomCalculator`) for two reasons the brief calls out
 * directly: attic access (pull-down stairs vs. a permanent staircase vs.
 * a scuttle hole) is a real, discrete cost driver with no equivalent
 * "tiered feature" shape in the shared factory, and structural
 * limitations (rafter vs. truss framing, available headroom) don't price
 * as a line item at all - they gate warnings/assumptions, the same
 * "user-driven multiplier + standing disclosure" pattern basement's
 * `CONDITION_ENVELOPE_MULTIPLIERS` and deck/patio's structural-complexity
 * handling established, not a fifth tiered feature.
 *
 * Per the brief's explicit instruction (mirroring "don't assume every
 * exterior project is a complete exterior renovation," applied here to
 * attics): insulation and finishing are two INDEPENDENT paths, not one
 * bundled scope. Insulation - typically the single biggest cost/value
 * driver for an attic - is priced whether or not the attic is being
 * finished into living space at all; `attic_finishing_scope` gates
 * finishing-only categories (the finishing base line item and flooring)
 * without touching insulation, electrical, or access pricing. An
 * insulation-only attic project is a fully valid, materially cheaper
 * path through this calculator, not a degraded case of a "real" attic
 * project.
 */

/**
 * Structural framing/headroom conditions scale the FINISHING base line
 * item only (not insulation, which is priced the same regardless of
 * whether the space will ever be finished) - trusses and tight headroom
 * both mean more supplemental framing/engineering work to make the space
 * usable as living space, the same "user-driven multiplier on one
 * specific anchor" pattern basement's condition-envelope multiplier
 * established in Phase 8.
 */
const STRUCTURAL_LIMITATION_FINISHING_MULTIPLIERS: Record<string, number> = {
  standard_rafters: 1.0,
  attic_trusses: 1.3,
  insufficient_headroom: 1.2,
  not_sure: 1.0
};

const INSULATION_ITEM_BY_TIER: Record<string, string | null> = {
  not_changing: null,
  standard_upgrade: "attic_insulation_standard",
  high_r_upgrade: "attic_insulation_high_r",
  spray_foam_premium: "attic_insulation_spray_foam"
};

const FLOORING_ITEM_BY_TIER: Record<string, string | null> = {
  not_changing: null,
  basic: "attic_flooring_basic",
  mid_range: "attic_flooring_mid",
  premium: "attic_flooring_premium"
};

const ELECTRICAL_ITEM_BY_TIER: Record<string, string | null> = {
  none: null,
  basic_lighting: "attic_electrical_basic_lighting",
  full_wiring: "attic_electrical_full_wiring"
};

const ACCESS_ITEM_BY_TIER: Record<string, string | null> = {
  no_change: null,
  pull_down_stairs: "attic_access_pull_down_stairs",
  permanent_staircase: "attic_access_permanent_staircase",
  scuttle_hole: "attic_access_scuttle_hole"
};

function rawString(input: EstimatorInput, key: string): string | undefined {
  const value = input.rawAnswers[key];
  return typeof value === "string" ? value : undefined;
}

export const calculateAttic: ProjectCalculatorFn = (input, catalog, regionalMultiplier) => {
  const applied: AppliedLineItem[] = [];
  const assumptions: EstimateAssumption[] = [];
  const exclusions: EstimateExclusion[] = [];
  const warnings: EstimateWarning[] = [];
  let anchorMissing = false;

  const finishLevel = input.finishLevel;
  const sqft = input.roomSize?.value;
  const finishingScope = rawString(input, "attic_finishing_scope") ?? "not_finishing";
  const isFinishing = finishingScope === "finishing_to_living_space";
  const structuralLimitation = rawString(input, "attic_structural_limitations");

  let finishingTypicalForPercentage = 0;

  if (sqft && sqft > 0) {
    // --- Insulation (tiered, sqft) - independent of finishing ----------------
    const insulationTier = rawString(input, "attic_insulation");
    const insulationItemId = insulationTier ? INSULATION_ITEM_BY_TIER[insulationTier] : null;
    if (insulationItemId) {
      const insulation = applyItem(catalog, insulationItemId, { quantity: sqft, finishLevel, regionalMultiplier });
      if (insulation) applied.push(insulation);
    } else {
      exclusions.push({ code: "excludes_attic_insulation", message: "No insulation upgrade was requested - excluded from this estimate." });
    }

    // --- Finishing base (anchor, only when finishing to living space) --------
    if (isFinishing) {
      const base = applyItem(catalog, "attic_finishing_base", { quantity: sqft, finishLevel, regionalMultiplier });
      if (base) {
        const limitationMultiplier = structuralLimitation ? STRUCTURAL_LIMITATION_FINISHING_MULTIPLIERS[structuralLimitation] ?? 1.0 : 1.0;
        let scoped = base;
        if (limitationMultiplier !== 1.0) {
          scoped = scaleAppliedItem(base, limitationMultiplier);
          assumptions.push({
            code: "structural_limitation_multiplier_applied",
            message: `Finishing cost adjusted by a ${limitationMultiplier}x multiplier for "${structuralLimitation}" structural framing/headroom conditions.`
          });
        }
        applied.push(scoped);
        finishingTypicalForPercentage = scoped.result.typical?.amount ?? 0;
      }

      // --- Flooring (tiered, sqft) - only relevant when finishing ------------
      const flooringTier = rawString(input, "attic_flooring");
      const flooringItemId = flooringTier ? FLOORING_ITEM_BY_TIER[flooringTier] : null;
      if (flooringItemId) {
        const flooring = applyItem(catalog, flooringItemId, { quantity: sqft, finishLevel, regionalMultiplier });
        if (flooring) applied.push(flooring);
      } else {
        exclusions.push({ code: "excludes_attic_flooring", message: "No attic flooring was requested - excluded from this estimate." });
      }
    } else {
      exclusions.push({
        code: "excludes_attic_finishing",
        message: "The attic is not being finished into living space - finishing/flooring costs were excluded from this estimate (insulation, if selected, is still priced above)."
      });
    }
  } else {
    anchorMissing = true;
  }

  // --- Electrical (tiered) -----------------------------------------------------
  const electricalTier = rawString(input, "attic_electrical");
  const electricalItemId = electricalTier ? ELECTRICAL_ITEM_BY_TIER[electricalTier] : null;
  if (electricalItemId) {
    const electrical = applyItem(catalog, electricalItemId, { finishLevel, regionalMultiplier });
    if (electrical) applied.push(electrical);
  } else {
    exclusions.push({ code: "excludes_attic_electrical", message: "No attic electrical work was requested - excluded from this estimate." });
  }

  // --- Access (tiered - a real cost driver, not cosmetic) -----------------------
  const accessTier = rawString(input, "attic_access");
  const accessItemId = accessTier ? ACCESS_ITEM_BY_TIER[accessTier] : null;
  if (accessItemId) {
    const access = applyItem(catalog, accessItemId, { finishLevel, regionalMultiplier });
    if (access) applied.push(access);
    if (isFinishing && accessTier === "scuttle_hole") {
      warnings.push({
        code: "attic_scuttle_hole_insufficient_for_living_space",
        message:
          "A scuttle hole was selected as attic access while also finishing the attic into living space. A simple scuttle hole is not a safe or code-compliant means of access/egress for habitable space in most Canadian jurisdictions - a permanent staircase (or at minimum pull-down stairs) is almost certainly required; confirm with your municipality before finalizing budget.",
        severity: "caution"
      });
    }
  } else {
    exclusions.push({ code: "excludes_attic_access", message: "No attic access improvement was requested - excluded from this estimate." });
  }

  // --- Demolition --------------------------------------------------------------
  if (input.workType.demolitionRequired === "yes" && findItem(catalog, "attic_demolition")) {
    const demolition = applyItem(catalog, "attic_demolition", { regionalMultiplier, baseAmountForPercentage: finishingTypicalForPercentage });
    if (demolition) applied.push(demolition);
  }

  // --- Accessibility -------------------------------------------------------------
  if (input.accessibility.required === "yes") {
    const accessibility = applyItem(catalog, "attic_accessibility", { finishLevel, regionalMultiplier });
    if (accessibility) applied.push(accessibility);
  }

  // --- Permit / design allowance -------------------------------------------------
  if (input.designAndPermits.needsDesignServices === "yes" || input.designAndPermits.permitLikelyRequired !== "no") {
    const permitDesign = applyItem(catalog, "attic_permit_design", { finishLevel, regionalMultiplier });
    if (permitDesign) applied.push(permitDesign);
  }

  // --- Special handling: standing, attic-specific disclosures -------------------
  // Per the same "surface it, don't leave it implicit" discipline
  // basement's/deck-patio's special-handling warnings established -
  // none of these can be resolved from a self-reported questionnaire.
  warnings.push({
    code: "attic_existing_conditions_unknown",
    message:
      "Existing insulation depth/type, roof ventilation, and any moisture or ice-damming history can't be assessed from a questionnaire, and materially affect both insulation and finishing costs. This estimate assumes normal, unobstructed attic conditions - an in-person inspection is recommended before finalizing budget.",
    severity: "caution"
  });

  if (isFinishing && structuralLimitation === "attic_trusses") {
    warnings.push({
      code: "attic_trusses_may_preclude_finishing",
      message:
        "Standard roof trusses (as opposed to rafter framing) are typically NOT designed to support finished living space and, in most cases, cannot simply be modified without a structural engineer's involvement - and in some cases finishing is not feasible at all without extensive (and expensive) supplemental framing. Confirm feasibility with a structural engineer before finalizing budget; this estimate's finishing multiplier accounts for additional framing cost but not the possibility that finishing is not feasible as planned.",
      severity: "caution"
    });
  }
  if (isFinishing && structuralLimitation === "insufficient_headroom") {
    warnings.push({
      code: "attic_headroom_code_minimum_unconfirmed",
      message:
        "Most Canadian building codes require a minimum ceiling height (commonly around 6'5\"/1.95m, over at least half the room's floor area) for a space to count as habitable/finished living space. Insufficient headroom was indicated - confirm actual measured height against your local code before finalizing budget; this estimate does not include raising the roofline.",
      severity: "caution"
    });
  }
  if (!structuralLimitation && isFinishing) {
    assumptions.push({
      code: "attic_structural_limitations_unconfirmed",
      message: "No structural framing/headroom information was provided for an attic being finished into living space - this estimate assumes standard rafter framing with adequate headroom, which may understate cost if that's not the case."
    });
  }

  const output: ProjectCalculationOutput = { applied, assumptions, exclusions, anchorMissing, warnings };
  return output;
};
